import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getApis } from 'library/Apis';
import { setPermissions, setUser } from './AuthActions';
import api from 'services/api';
import SnackbarUtils from '../../utils/SnackbarUtils'
import { AUTH_API_VERSION, LOCAL_STORAGE_REDIRECT_URI } from './AuthReducer';
import userManager from './oidc.config';

const env = window.env;

export const getAuthApiPath = (version: string) =>
  (version ? 'v' + version : 'v1') + '/usuario';

export const getToken = async (): Promise<string | null> => {
  const user = await userManager.getUser();
  return user && !user.expired ? user.access_token : null;
};

export const isAuthenticated = async (): Promise<boolean> => {
  const user = await userManager.getUser();
  return !!(user && !user.expired);
};

export const getValidUser = async () => {
  let user = await userManager.getUser();
  if (!user || user.expired) {
    try {
      user = await userManager.signinSilent();
    } catch (error) {
      return null;
    }
  }
  return user;
};

const logout = async () => {
  const user = await userManager.getUser();
  if (user) {
    await userManager.removeUser();
    await userManager.clearStaleState();
    await userManager.signoutRedirect({
      id_token_hint: user.id_token,
      post_logout_redirect_uri: env.auth.logoutUrl,
    });
  }
};

export const loadUserAndPermissions = () => {
  return async (dispatch: any) => {
    try {
      const user = await getValidUser();

      if (!user) {
        const publicRoutes = ['/cadastro', '/auth/login'];
        const currentPath = window.location.pathname;
        const isPublicRoute = publicRoutes.includes(currentPath);

        if (!isPublicRoute) {
          SnackbarUtils.enqueueSnackbar({
            message: 'Sessão expirada. Redirecionando para login...',
            options: { variant: 'error' },
          });

          await userManager.removeUser();
          await userManager.clearStaleState();
          localStorage.clear();
          sessionStorage.clear();

          await userManager.signinRedirect({
            extraQueryParams: { prompt: 'login' }
          });
        }

        return;
      }

      const token = user.access_token;
      if (!token) {
        throw new Error('Token de acesso ausente após signinSilent');
      }

      const endpoint = getApis().apis.bff + getAuthApiPath(AUTH_API_VERSION);
      const response = await api.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const userData = response.data;
      dispatch(setUser({
        access_token: token,
        token_type: user.token_type,
        profile: userData,
      }));
      dispatch(setPermissions(userData));
    } catch (error: any) {
      if (error.response) {
        const status = error.response.status;
        if (status === 403) {
          SnackbarUtils.enqueueSnackbar({
            message: 'Acesso negado. Redirecionando para login...',
            options: { variant: 'error' },
          });
          await userManager.removeUser();
          await userManager.clearStaleState();
          localStorage.clear();
          sessionStorage.clear();
          await userManager.signinRedirect({
            extraQueryParams: { prompt: 'login' }
          });
          return;
        }
      } else {
        SnackbarUtils.enqueueSnackbar({
          message: 'Erro ao carregar usuário, Redirecionando para login...',
          options: {
            variant: 'error',
          },
        });
        await logout();
        return;
      }
    }
  };
};

export const AuthCallback = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const processLogin = async () => {
      try {
        await userManager.signinRedirectCallback();
        await userManager.signinSilent();
        await dispatch(loadUserAndPermissions());
        const redirectUri = localStorage.getItem(LOCAL_STORAGE_REDIRECT_URI);
        if (redirectUri) {
          history.push(redirectUri);
          localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URI);
        } else {
          history.push(`/projetos/pufv`);
        }
      } catch (error) {
        SnackbarUtils.enqueueSnackbar({
          message: "Sessão inválida. Redirecionando...",
          options: { variant: 'error' },
        });
        await logout();
        return;
      }
    };
    processLogin();
  }, [dispatch, history]);
};

const AuthService = () => {

  const signinRedirect = async () => {
    const user = await userManager.getUser();

    if (user) {
      await userManager.signoutRedirect({
        id_token_hint: user.id_token,
        post_logout_redirect_uri: env.auth.redirectUrl,
      });
      return;
    }
    await userManager.signinRedirect({
      extraQueryParams: { prompt: "login" }
    });
  };

  const signinRedirectCallback = () => {
    userManager.signinRedirectCallback();
  };

  return {
    signinRedirectCallback,
    signinRedirect,
    logout,
  };
};

export default AuthService;
